import { AuthenticationDetails, CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';

const { VUE_APP_COGNITO_CLIENT_ID, VUE_APP_COGNITO_USER_POOL_ID, VUE_APP_USER_GROUP_NAME } = process.env;

const createUserPool = () => {
  return new CognitoUserPool({
    UserPoolId: VUE_APP_COGNITO_USER_POOL_ID,
    ClientId: VUE_APP_COGNITO_CLIENT_ID
  });
}

const createCognitoUser = ({ email }) => {
  const userPool = createUserPool();

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool
  });

  return cognitoUser;
}

const userHasPermissions = (session) => {
  if(session) {
    let hasPermissions = false;

    const userGroups = session.accessToken.payload["cognito:groups"];
    if(userGroups && userGroups.includes(VUE_APP_USER_GROUP_NAME)) {  
      hasPermissions = true;
    }
  
    return hasPermissions;
  }
}

export default {
  async login({ username, password, handleResetRequired, handleError }) {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password
    });

    const cognitoUser = createCognitoUser({ email: username });
    cognitoUser.setAuthenticationFlowType('USER_SRP_AUTH');

    cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          userHasPermissions(result) ? 
            window.location.reload() : 
            handleError('You do not have permissions to be here.')
        },
        onFailure: (error) => {
          // Check if user needs to reset their password
          if(error.code === 'PasswordResetRequiredException') {
            this.resetPassword({username})
            handleResetRequired()
          }
          // Tries via USER_PASSWORD_AUTH to migrate user to cognito
          else if (error.code === 'UserNotFoundException') {
            this.loginWithPasswordFlow({username, password, handleError, handleResetRequired });
          }
          else {
            handleError(error);
          }
        }
      })
  },

  loginWithPasswordFlow({ username, password, handleResetRequired, handleError }) {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password
    });

    const cognitoUser = createCognitoUser({ email: username });
    cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    return cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => result,
        onFailure: (error) => {
          // Forces a password reset to migrate users to Cognito
          if(error.code === 'PasswordResetRequiredException') {
            this.resetPassword({username, handleError});
            handleResetRequired();
          }
          // Ensures that if the user still doesn't exist it tells the user their username or password is incorrect.
          else if (error.code === 'UserNotFoundException') {
            handleError('The username or password is incorrect.');
          }
          else {
            handleError(error);
          }
        }
        })
  },

  resetPassword({ username, handleError, handleSuccess }) {
    const cognitoUser = createCognitoUser({ email: username });

    return cognitoUser.forgotPassword({
        onSuccess: () => handleSuccess(),
        onFailure: (error) => handleError(error)
    })
  },

  confirmPassword({ verificationCode, newPassword, email, handleNavigateToLogin, handleError }) {
    const cognitoUser = createCognitoUser({ email });

    return cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => handleNavigateToLogin(),
        onFailure: (err) => handleError(err)
      })
  },

  sendResetCode({ email, handleNavigateToResetPassword, handleError }) {
    const cognitoUser = createCognitoUser({ email });

    return cognitoUser.forgotPassword({
        onSuccess: () => handleNavigateToResetPassword(),
        onFailure: (err) => handleError(err)
      })
  },

  logout() {
    const cognitoUser = createUserPool().getCurrentUser();
      if (cognitoUser !== null) {
       cognitoUser.signOut();
      }
    localStorage.removeItem('profile');
  },

  getUserSession() {
    const userPool = createUserPool();
    let cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      return;
    }
    let userSession;

    cognitoUser.getSession(function (error, session) {
      if (error) {
        console.log(error);
        return;
      }
      if (session) {
        userSession = session;
      }
    });
    return userSession;
  },

  async checkUserSessionIsValid () {
    try {
      const userPool = createUserPool();
      let cognitoUser = userPool.getCurrentUser();

      if (cognitoUser) {
        // The getSession call needs to be wrapped in a Promise so that it doesn't fail when it refreshes 
        // the token in the background. See: https://github.com/aws-amplify/amplify-js/issues/3613
        const userSession = await new Promise((resolve, reject) => {
          cognitoUser.getSession(function (error, session) {
            if (error) {
              reject(error);
            }
            resolve(session);
          })
        })
    
        if (userSession && userSession.isValid()) {
          return userHasPermissions(userSession);
        }
      } 
      else {
        return false;
      }
    }
    catch (error) {
      console.log(error);
    }
  }
};
